// The application's OWN payments settings — what a `payment_settings` block
// talks to, as opposed to the builder's Advanced → Payments screen.
//
// Same service behind both (see `backend/app/routers/app_gateway_settings.py`),
// so a field accepted on one is accepted on the other. Two things differ, and
// they are the reason this module exists rather than a flag on `appPayments`:
//
//  · WHO MAY — the project owner or a FUNC-ADMIN on the application, rather
//    than a platform member holding DEPLOY. A customer's own administrator
//    typically holds no platform permission at all.
//  · WHERE FROM — this prefix is the only merchant-configuration path
//    `middleware/domain_scope` admits on a customer's domain. The builder's
//    router is not admitted there, because it also carries the money ledger,
//    the refunds and the webhook history.
//
// Secrets remain write-only: a read hands back a masked tail, an omitted
// secret means "unchanged", and "-" clears.
import api from './client'
import type {
  GatewayListResponse, GatewayProvider, GatewayUpdate, PaymentGateway,
} from './appPayments'

const BASE = '/app-gateway-settings'

export async function listAppGateways(project: string): Promise<GatewayListResponse> {
  const { data } = await api.get(`${BASE}/${encodeURIComponent(project)}`)
  return data
}

export async function saveAppGateway(
  project: string, body: GatewayUpdate,
): Promise<PaymentGateway> {
  const { data } = await api.put(`${BASE}/${encodeURIComponent(project)}`, body)
  return data.gateway
}

export async function testAppGateway(
  project: string, provider: GatewayProvider,
): Promise<{ ok: boolean; error?: string; verified_at?: string | null }> {
  const { data } = await api.post(
    `${BASE}/${encodeURIComponent(project)}/${provider}/test`)
  return data
}

export async function deleteAppGateway(
  project: string, provider: GatewayProvider,
): Promise<void> {
  await api.delete(`${BASE}/${encodeURIComponent(project)}/${provider}`)
}

/** Accept cash on delivery, or stop — the same project setting the builder's
 *  screen writes. Needs no gateway. */
export async function setAppCashOnDelivery(
  project: string, enabled: boolean,
): Promise<boolean> {
  const { data } = await api.put(
    `${BASE}/${encodeURIComponent(project)}/cod`, { enabled })
  return !!data.cod_enabled
}
